import React, { useState, useEffect, useRef } from 'react';
import { Camera, X, Play, RefreshCw, AlertCircle, Upload } from 'lucide-react';
import { Icon } from "./Icon";

// interface StreamStatus {
//     streaming: boolean;
//     timestamp: number;
// }

// interface FrameData {
//     frame: string;
//     timestamp: number;
// }

interface PersonFormData {
    nombre: string;
    proveedor: string;
    tipo: number;
    banco_s3: boolean;
    imagen: File | null;
}

// interface UserFormResponse {
//     success: boolean,
//     personId: string
// }

interface UserFormCardProps {
    creating: boolean;
    isVisible: boolean;
    onClose: () => void;
    onSubmit: (formData: PersonFormData) => Promise<{ success: boolean, personId?: string, message?: string }>;
    initialData?: Partial<PersonFormData>;
}

// Modal de captura RTSP
const RTSPCaptureModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onCapture: (imageFile: File) => void;
}> = ({ isOpen, onClose, onCapture }) => {
    const [isStreaming, setIsStreaming] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [streamMethod, setStreamMethod] = useState<'mjpeg' | 'polling'>('mjpeg');
    const [currentFrame, setCurrentFrame] = useState<string | null>(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const FLASK_SERVER = import.meta.env.VITE_FLASK_SERVER;

    const startMJPEGStream = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${FLASK_SERVER}/start_stream`);
            if (!response.ok) throw new Error('Error al iniciar stream');

            if (imgRef.current) {
                imgRef.current.crossOrigin = 'anonymous';
                imgRef.current.src = `${FLASK_SERVER}/video_feed?t=${Date.now()}`;
                imgRef.current.onload = () => {
                    setIsStreaming(true);
                    setLoading(false);
                };
                imgRef.current.onerror = () => {
                    // setError('Error al cargar el stream de video');
                    setLoading(false);
                };
            }
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Error desconocido'}`);
            setLoading(false);
        }
    };

    const stopStream = async () => {
        try {
            setLoading(true);

            await fetch(`${FLASK_SERVER}/stop_stream`);

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            if (imgRef.current) {
                imgRef.current.src = '';
            }

            setCurrentFrame(null);
            setIsStreaming(false);
            setError(null);
        } catch (err) {
            setError(`Error al detener: ${err instanceof Error ? err.message : 'Error desconocido'}`);
        } finally {
            setLoading(false);
        }
    };

    const captureAndClose = async () => {
        if (!canvasRef.current) return;

        setIsCapturing(true);

        try {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('No se pudo obtener el contexto del canvas');
            }

            let imageSource: HTMLImageElement | null = null;

            if (streamMethod === 'mjpeg' && imgRef.current && imgRef.current.src) {
                // Crear una nueva imagen para evitar problemas de CORS
                const tempImg = new Image();
                tempImg.crossOrigin = 'anonymous';
                await new Promise((resolve, reject) => {
                    tempImg.onload = resolve;
                    tempImg.onerror = reject;
                    tempImg.src = imgRef.current!.src;
                });
                imageSource = tempImg;
            } else if (streamMethod === 'polling' && currentFrame) {
                const tempImg = new Image();
                await new Promise((resolve, reject) => {
                    tempImg.onload = resolve;
                    tempImg.onerror = reject;
                    tempImg.src = currentFrame;
                });
                imageSource = tempImg;
            }

            if (!imageSource) {
                throw new Error('No hay imagen disponible para capturar');
            }

            canvas.width = imageSource.naturalWidth || imageSource.width;
            canvas.height = imageSource.naturalHeight || imageSource.height;
            ctx.drawImage(imageSource, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error('Error al crear blob'));
                }, 'image/jpeg', 0.9);
            });

            // Crear archivo desde blob
            const file = new File([blob], `captura_${Date.now()}.jpg`, { type: 'image/jpeg' });

            // Detener stream y cerrar modal
            await stopStream();
            onCapture(file);
            onClose();

        } catch (err) {
            setError(`Error al capturar: ${err instanceof Error ? err.message : 'Error desconocido'}`);
        } finally {
            setIsCapturing(false);
        }
    };

    const handleClose = async () => {
        await stopStream();
        onClose();
    };

    useEffect(() => {
        setStreamMethod('mjpeg');
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current); // Limpia el intervalo si existe
            }
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            const timeout = setTimeout(() => {
                startMJPEGStream();
            }, 1500);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [isOpen])


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm  flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-auto max-h-[80vh] overflow-hidden flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <div className='flex flex-row'>
                        <h2 className="text-xl font-bold text-gray-800">Capturar Fotografía</h2>
                        <div className="text-center ml-2">
                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${isStreaming ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                <div className={`w-2 h-2 rounded-full ${isStreaming ? 'bg-green-500' : 'bg-gray-400'}`} />
                                {isStreaming ? 'Conectado' : 'Desconectado'}
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Contenido */}
                <div className="flex-1 p-6">


                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                            <div className="flex items-center gap-2 text-red-800">
                                <AlertCircle size={16} />
                                <span className="text-sm">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Video Display */}
                    <div className="bg-black rounded-lg overflow-hidden">
                        {streamMethod === 'mjpeg' ? (
                            <img
                                ref={imgRef}
                                alt="RTSP Stream"
                                className="w-full h-auto max-h-96 object-contain"
                                style={{ display: isStreaming ? 'block' : 'none' }}
                            />
                        ) : (
                            currentFrame && (
                                <img
                                    src={currentFrame}
                                    alt="RTSP Frame"
                                    className="w-full h-auto max-h-96 object-contain"
                                />
                            )
                        )}

                        {!isStreaming && !loading && (
                            <div className="h-80 flex items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <Play size={48} className="mx-auto mb-2 opacity-50" />
                                    <p>En un momento inicia la transmisión</p>
                                </div>
                            </div>
                        )}

                        {loading && (
                            <div className="h-80 flex items-center justify-center text-gray-500">
                                <div className="text-center">
                                    <RefreshCw size={48} className="mx-auto mb-2 animate-spin" />
                                    <p>Cargando stream...</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Controles */}
                    <div className="mt-4 space-y-3">
                        <div className="flex flex-wrap gap-2 items-center justify-center">
                            {/* <select
                                value={streamMethod}
                                onChange={(e) => setStreamMethod(e.target.value as 'mjpeg' | 'polling')}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                                disabled={isStreaming}
                            >
                                <option value="mjpeg">MJPEG Stream (Recomendado)</option>
                                <option value="polling">Polling de Frames</option>
                            </select>

                            <button
                                onClick={handleStart}
                                disabled={isStreaming || loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
                            >
                                <Play size={16} />
                                {loading ? 'Iniciando...' : 'Iniciar Stream'}
                            </button> */}

                            {/* <button
                                onClick={stopStream}
                                disabled={!isStreaming || loading}
                                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                            >
                                <Square size={16} />
                                Detener
                            </button> */}

                            <button
                                onClick={captureAndClose}
                                disabled={!isStreaming || isCapturing}
                                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 font-medium"
                            >
                                <Camera size={16} />
                                {isCapturing ? 'Capturando...' : 'Capturar '}
                            </button>
                        </div>
                    </div>

                    <canvas ref={canvasRef} className="hidden" />
                </div>

            </div>
        </div>
    );
};

// Componente principal del formulario
export const UserFormCard: React.FC<UserFormCardProps> = ({
    creating,
    isVisible,
    onClose,
    onSubmit,
    initialData = {}
}) => {
    const [formData, setFormData] = useState<PersonFormData>({
        nombre: initialData.nombre || '',
        proveedor: initialData.proveedor || '',
        tipo: initialData.tipo || 0,
        banco_s3: initialData.banco_s3 || false,
        imagen: initialData.imagen || null
    });
    const [dragActive, setDragActive] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [showRTSPModal, setShowRTSPModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [stateSendForm, setStateSendForm] = useState(false);
    const [notification, setNotification] = useState(false)

    // Efecto para cargar imagen inicial si existe
    React.useEffect(() => {
        if (initialData.imagen) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(initialData.imagen);
        }
    }, [initialData.imagen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    const inputValue = type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : value;
    
    setFormData(prev => ({
        ...prev,
        [name]: inputValue
    }));
};

    const handleImageChange = (file: File) => {
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({
                ...prev,
                imagen: file
            }));

            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRTSPCapture = (capturedFile: File) => {
        handleImageChange(capturedFile);
        setShowRTSPModal(false);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageChange(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleImageChange(e.target.files[0]);
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async () => {
        if (!formData.nombre || !formData.proveedor) {
            alert('Por favor, completa todos los campos obligatorios.');
            return;
        }

        if (!formData.tipo || formData.tipo === 0) {
            alert('Por favor, seleccione un tipo.');
            return;
        }

        const response = await onSubmit(formData);
        if (response.success) {
            setStateSendForm(true);
            removeDataForm();
        } else {
            setStateSendForm(true);
        }
        setNotification(true);
    };

    const removeDataForm = () => {

        setFormData({
            nombre: initialData.nombre || '',
            proveedor: initialData.proveedor || '',
            tipo: initialData.tipo || 0,
            banco_s3: initialData.banco_s3 || false,
            imagen: initialData.imagen || null
        });
        setImagePreview(null);
        const timeout = setTimeout(() => {
            setNotification(false);
            onClose();
        }, 1500);
        return () => {
            clearTimeout(timeout);
        };
    }

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            imagen: null
        }));
        setImagePreview(null);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full mx-auto mb-8">
                <div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4">
                        {/* Columna Izquierda - Inputs */}
                        <div className="space-y-2">
                            <div>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 text-sm dark:bg-gray-700 dark:text-white border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Nombres..."
                                />
                            </div>

                            <div>
                                <input
                                    type="text"
                                    name="proveedor"
                                    value={formData.proveedor}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 text-sm dark:bg-gray-700 dark:text-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Proveedor..."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <select
                                        name="tipo"
                                        value={formData.tipo}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 text-sm dark:bg-gray-700 dark:text-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    >
                                        <option value={0}>Seleccionar tipo...</option>
                                        <option value={1}>Programada</option>
                                        <option value={2}>Recurrente</option>
                                        <option value={3}>No Programada</option>
                                    </select>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        name="banco_s3"
                                        checked={formData.banco_s3 || false}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                        id="banco_s3"
                                    />
                                    <label
                                        htmlFor="banco_s3"
                                        className="text-sm font-medium text-gray-900 dark:text-gray-300 cursor-pointer"
                                    >
                                        Habilitar para S3
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Columna Derecha - Imagen */}
                        <div className="space-y-4">
                            <div
                                className={`relative h-auto lg:h-28 w-full border-2 border-dashed rounded-lg p-2 text-center transition-all duration-200 ${dragActive
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400'
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />

                                {imagePreview ? (
                                    <div className="relative">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="h-24 w-24 object-cover rounded-lg mx-auto"
                                        />
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="py-4 grid grid-cols-1 lg:grid-cols-2 justify-center items-center">
                                        <div className="flex flex-col gap-2">
                                            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-gray-600 dark:text-gray-300 mb-3 text-xs">
                                                Arrastra una imagen aquí
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2 mx-2">
                                            <div className="text-gray-400 text-xs">o</div>
                                            <button
                                                type="button"
                                                onClick={openFileDialog}
                                                className="bg-gray-100 dark:bg-gray-600 dark:text-white hover:bg-gray-200 text-gray-700 px-3 py-3 rounded-lg transition-colors duration-200 text-xs"
                                            >
                                                Seleccionar archivo
                                            </button>

                                        </div>
                                        {/* <div className="flex flex-col gap-2 mx-2">
                                            <div className="text-gray-400 text-xs">o</div>
                                            <button
                                                type="button"
                                                onClick={() => setShowRTSPModal(true)}
                                                className="flex items-center justify-center gap-1 bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-3 rounded-lg transition-colors duration-200 text-xs"
                                            >
                                                <Camera size={12} />
                                                Capturar desde cámara
                                            </button>
                                        </div> */}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-center lg:justify-end flex-col sm:flex-row mt-2 pt-2 border-t border-gray-200">
                        {notification && (
                            <div className={`flex flex-row border ${stateSendForm ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"}    text-bold mr-2   p-2 text-sm rounded-lg`}>
                                <Icon name={`${stateSendForm ? "check" : "close"}`} className="w-4 h-4 mr-2" />
                                <span >{stateSendForm ? "Usuario Agregado!!!" : "Error al crear usuario"}</span>
                            </div>
                        )}

                        <button
                            type="button"
                            disabled={creating}
                            onClick={handleSubmit}
                            className={`flex flex-row text-sm ${creating ? "bg-blue-400" : "bg-blue-600"
                                } bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2`}
                        >
                            {creating && (
                                <Icon name="loading" className="w-3 h-3 lg:w-4 lg:h-4 text-white animate-spin" />
                            )}
                            {creating ? "Creando..." : "Crear Usuario"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal RTSP */}
            <RTSPCaptureModal
                isOpen={showRTSPModal}
                onClose={() => setShowRTSPModal(false)}
                onCapture={handleRTSPCapture}
            />
        </>
    );
};